import { contactInfo, socialLinks } from "@/mocks/site";
import { googleRating, googleReviews } from "@/mocks/reviews";
import { getServiceDetail } from "@/mocks/services";

const SITE = "https://laharipackersandmovers.in";

/**
 * Structured data requires absolute URLs. Any relative asset path (a leading
 * "/") is resolved against the site origin so JSON-LD stays valid for Google.
 */
export const absoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE}${path.startsWith("/") ? "" : "/"}${path}`;
};

/** Social profiles that are real, working profile URLs (used for schema sameAs). */
const validSocialProfiles = socialLinks
  .map((social) => social.href)
  .filter((href) => /^https?:\/\/[^/]+\.[^/]+\/.+/i.test(href));

const areaNames = (getServiceDetail("local-shifting")?.areas ?? []).map((area) => area.name);

export interface CrumbItem {
  label: string;
  href?: string;
}

/** Site-wide business profile: address, geo, opening hours, price range, ratings & reviews. */
export const buildBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "MovingCompany",
  "@id": `${SITE}/#business`,
  name: contactInfo.brand,
  alternateName: "Lahari Packers & Movers",
  description:
    "Lahari Packers and Movers is a Bangalore based relocation company offering house shifting, office relocation, local shifting, storage and warehousing services across Bangalore, Mysore and Tumkur since 2009.",
  url: `${SITE}/`,
  logo: "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/0b3b31cd6a81a00dadd32efb0f200320.png",
  image:
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20banner%20of%20a%20professional%20moving%20crew%20in%20uniform%20loading%20neatly%20wrapped%20cardboard%20boxes%20and%20furniture%20into%20a%20clean%20modern%20moving%20truck%20parked%20outside%20a%20bright%20Bangalore%20residential%20apartment%20building%2C%20warm%20golden%20hour%20light%2C%20deep%20green%20and%20cream%20color%20palette%2C%20clear%20uncluttered%20composition%2C%20photorealistic%20premium%20relocation%20marketing%20photography%2C%20no%20text%20letters%20or%20logos&width=1200&height=630&seq=lahari-social-cover-2026&orientation=landscape",
  telephone: contactInfo.phone,
  email: contactInfo.email,
  priceRange: "₹₹",
  currenciesAccepted: "INR",
  paymentAccepted: "Cash, UPI, Bank Transfer",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Anikethana Nagara, Kannahalli",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    postalCode: "560112",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 12.982166422556176,
    longitude: 77.44442434910671,
  },
  hasMap: contactInfo.mapLink,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:30",
      closes: "18:30",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Sunday",
      opens: "10:30",
      closes: "14:30",
    },
  ],
  areaServed: areaNames.map((name) => ({ "@type": "Place", name: `${name}, Bangalore` })),
  sameAs: validSocialProfiles,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: googleRating.score,
    bestRating: "5",
    worstRating: "1",
    reviewCount: 1300,
  },
  review: googleReviews.map((item) => ({
    "@type": "Review",
    author: { "@type": "Person", name: item.name },
    reviewRating: { "@type": "Rating", ratingValue: item.rating, bestRating: "5" },
    reviewBody: item.quote.replace(/\s+/g, " ").trim(),
  })),
});

/** Breadcrumb trail for the current page. */
export const buildBreadcrumbSchema = (items: CrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.label,
    ...(item.href ? { item: item.href === "/" ? `${SITE}/` : `${SITE}${item.href}` } : {}),
  })),
});

/** Item list of every locality we serve, used on the Areas We Serve hub. */
export const buildAreaListSchema = (areas: { name: string; slug: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Areas served by Lahari Packers and Movers in Bangalore",
  itemListElement: areas.map((area, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: `${area.name}, Bangalore`,
    url: `${SITE}/areas-we-serve/${area.slug}`,
  })),
});

/**
 * Site-wide WebSite schema. Ties the whole site to the business entity
 * (publisher references the #business @id) so Google understands the site's
 * identity and preferred name in search results.
 */
export const buildWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: `${SITE}/`,
  name: contactInfo.brand,
  alternateName: "Lahari Packers & Movers",
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE}/#business` },
});

/**
 * Human-readable labels for the top-level routes, used to build breadcrumb
 * structured data site-wide from a single place.
 */
const ROUTE_BREADCRUMB_LABELS: Record<string, string> = {
  "/": "Home",
  "/about": "About Us",
  "/services": "Services",
  "/warehouse": "Warehouse",
  "/highlights": "Highlights",
  "/gallery": "Gallery",
  "/contact": "Contact Us",
  "/request-a-quote": "Request a Quote",
  "/terms": "Terms & Conditions",
  "/privacy": "Privacy Policy",
};

/**
 * Build a breadcrumb trail from any pathname. Returns an empty array for pages
 * that emit their own (more specific) breadcrumb — the service detail pages and
 * every areas-we-serve page — so no page ever ships two breadcrumb blocks.
 */
export const buildRouteBreadcrumbs = (pathname: string): CrumbItem[] => {
  const path = (pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/");

  if (path.startsWith("/areas-we-serve")) return [];
  if (/^\/services\/.+/.test(path)) return [];

  const label = ROUTE_BREADCRUMB_LABELS[path];
  if (!label) return [];
  if (path === "/") return [{ label: "Home", href: "/" }];

  return [
    { label: "Home", href: "/" },
    { label, href: path },
  ];
};